/**
 * fetch-guard.ts — a synchronous, default-deny fetch interceptor.
 *
 * WHY: some reused leaves (notably ClientView) call `fetch("/api/checkin")` with
 * INLINE string literals that cannot be aliased away. On the static demo host
 * there is no backend, so an un-intercepted call would 404/405 and the leaf's
 * `catch {}` would swallow it — a silent failure that breaks the demo's pre/post
 * check-in story.
 *
 * This module installs its override at IMPORT TIME (top-level side effect). It MUST
 * be the FIRST import in main.tsx so it runs before any leaf module body evaluates.
 *
 * Policy: NOTHING on `/api/*` ever reaches a real network. `/api/checkin` gets a
 * canned CSRF token + 200 no-op; any other `/api/*` resolves to a 200 no-op. Every
 * non-/api path (the demo's own static assets) passes through to the real fetch.
 */

const realFetch = typeof window !== "undefined" ? window.fetch.bind(window) : undefined;

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json" },
  });
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installFetchGuard(): void {
  if (typeof window === "undefined") return;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = urlOf(input);
    // Resolve to an absolute pathname for robust matching.
    let path = url;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      /* keep raw url */
    }

    if (path.startsWith("/api/checkin")) {
      // GET returns a CSRF token the leaf expects; POST is a no-op success.
      return jsonResponse({ csrfToken: "demo-csrf", ok: true });
    }
    if (path.startsWith("/api/")) {
      // Default-deny every other backend call — never hit a real network.
      return jsonResponse({ ok: true, demo: true }, 200);
    }

    // Same-origin static assets only (CSP connect-src 'self'): allow through.
    return realFetch ? realFetch(input, init) : jsonResponse({}, 200);
  };
}

// Auto-install at IMPORT TIME. Keeping this as a module-load side effect means a
// side-effect import (`import "./shims/fetch-guard"`) placed FIRST in main.tsx
// guarantees the override is live before any leaf module body can call fetch.
installFetchGuard();
